import Key from "./Key";
import { KeyStackProps } from "./interfaces";

/**
 * KeyStack component that displays a stack of keys.
 *
 * @component
 * @param  props - The props for the KeyStack component.
 * @param  props.keys  - An array of key objects, each containing a type and value.
 * @param props.status - The status of the keys, which determines their styling.
 * @param props.isWhite - Whether the keys are white.
 * @example
 * // Example usage of the KeyStack component
 * import { KeyStack } from './KeyStack';
 *
 * const App = () => {
 *
 *   return (
 *     <div>
 *       <KeyStack
 *         keys={[
 *           { type: "letter", value: "⌘" },
 *           { type: "letter", value: "K" },
 *         ]}
 *         status="active"
 *       />
 *       <KeyStack
 *         keys={[
 *           { type: "text", value: "ctrl" },
 *           { type: "letter", value: "I" },
 *         ]}
 *         status="disabled"
 *       />
 *       <KeyStack
 *         keys={[
 *           { type: "text", value: "ctrl" },
 *           { type: "letter", value: "↑" },
 *           { type: "letter", value: "I" },
 *         ]}
 *       />
 *     </div>
 *   );
 * };
 *
 * export default App;
 */
const KeyStack = ({ keys, status, isWhite }: KeyStackProps) => {
  return (
    <div
      className="flex gap-layout-spacing-4"
      role="group"
      aria-label="Keyboard shortcut sequence"
    >
      {keys.map(key => (
        <Key
          key={key.value}
          type={key.type as "text"}
          value={key.value}
          status={status}
          isWhite={isWhite}
        />
      ))}
    </div>
  );
};

KeyStack.displayName = "KeyStack";

export default KeyStack;
